import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Image, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, SettingsItem, SettingsItemContent, SettingsItemDescription, SettingsItemTitle, Spacer, } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { formatSortCode } from 'utils/formatting';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import Modal from 'utils/packages/Modal';
import { useConnectedAccount } from '../hooks/selectors';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { acceptAutoInvestMandate, clearOnboardingState } from '../actions';
import { isWeb } from '../../../constants';
const directDebitLogo = require('features/invest/img/directDebitLogo.png');
const AutoinvestConfirmBankDetailsScreen = withAnimatedNavigationHeader(({ route, scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const account = 'account' in route.params ? route.params.account : undefined;
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const [, , requesting, fetch] = useFetchOld(muteErrorAndResult);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const email = useAppSelector((state) => state.user.user?.email ?? '');
    const [checked, setChecked] = useState(false);
    const connectedAccount = useConnectedAccount();
    const onboardingState = useAppSelector((state) => state.invest.onboarding);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const bankAccount = connectedAccount || account;
    const navigateToNextScreen = useNavigateToNextScreen();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const onCheckPress = useCallback(() => {
        setChecked((prev) => !prev);
    }, []);
    const sortCode = useMemo(() => {
        if (bankAccount) {
            const selected = 'ukSortCode' in bankAccount ? bankAccount.ukSortCode : bankAccount.sortCode ?? '';
            return selected.replace(/-/g, '');
        }
        return undefined;
    }, [bankAccount]);
    const accountNumber = useMemo(() => {
        if (bankAccount) {
            return 'ukAccountNumber' in bankAccount ? bankAccount.ukAccountNumber : bankAccount.accountNumber ?? '';
        }
        return undefined;
    }, [bankAccount]);
    const handleContinue = useCallback(async () => {
        if (route.params && 'onDirectDebitSetup' in route.params) {
            if (sortCode && accountNumber) {
                const response = await fetch(acceptAutoInvestMandate(sortCode, accountNumber));
                if (response?.mandateStatus === 'accepted') {
                    navigation.pop(2);
                    route.params.onDirectDebitSetup?.();
                    return;
                }
            }
            Modal.showConfirmation({
                title: 'We couldn’t set up your Direct Debit',
                subtitle: 'Please try again or contact support.',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        if (!tradingAccountId) {
            if (onboardingState) {
                if (bankAccount) {
                    dispatch({
                        type: 'SAVE_ONBOARDING_STATE',
                        fundingAccount: bankAccount,
                    });
                    navigateToNextScreen();
                    return;
                }
            }
            dispatch(clearOnboardingState());
            navigation.popToTop();
            return;
        }
        if (route.params) {
            navigation.navigate('AutoinvestReview', {
                accountType: route.params.accountType,
                collectionDate: route.params.collectionDate,
                amount: route.params.amount,
                allocations: route.params.allocations,
                planId: route.params.planId,
            });
        }
    }, [bankAccount, navigateToNextScreen, onboardingState, route.params, tradingAccountId]);
    const formattedSortCodeAndAccountNumber = useMemo(() => {
        if (bankAccount) {
            if ('sortCode' in bankAccount) {
                return `${formatSortCode(bankAccount.sortCode)} ${bankAccount?.accountNumber}`;
            }
            if ('ukSortCode' in bankAccount) {
                return `${formatSortCode(bankAccount.ukSortCode)} ${bankAccount?.ukAccountNumber}`;
            }
        }
        return '';
    }, [bankAccount]);
    return (<AppView type="secondary" withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title="Check your details are correct"/>
            <CardView noMarginTop>
              <SettingsItem>
                <SettingsItemContent>
                  <SettingsItemDescription>Account</SettingsItemDescription>
                  <SettingsItemTitle>{bankAccount?.bankName || 'Linked bank'}</SettingsItemTitle>
                </SettingsItemContent>
              </SettingsItem>
              <SettingsItem style={styles.noMarginTop}>
                <SettingsItemContent>
                  <SettingsItemDescription>Sort code and account number</SettingsItemDescription>
                  <SettingsItemTitle>{formattedSortCodeAndAccountNumber}</SettingsItemTitle>
                </SettingsItemContent>
              </SettingsItem>
            </CardView>

            <CardView withPadding noMarginTop>
              <Text TextThin-14 Secondary>
                Payments will show as “SECCL CUSTODY LTD” on your bank statement. After you complete the setup, an email
                confirmation will be sent to {email} within 3 days.
              </Text>
              <Spacer.H16 />
              <View style={styles.footer}>
                <View style={styles.flex}>
                  <Text TextThin-14 Secondary>
                    You may cancel this Direct Debit at any time by contacting Emma or your bank.
                  </Text>
                </View>
                <Spacer.W16 />
                <Image source={directDebitLogo} style={styles.img}/>
              </View>
            </CardView>

            <TouchableBounceCard onPress={onCheckPress} onDark withPadding noMarginTop>
              <View style={styles.row}>
                <RadioButton type="multiple" isChecked={checked}/>
                <Spacer.W16 />
                <View style={styles.flex}>
                  <Text TextThin-14>
                    I confirm that I am the account holder and am authorised to set up Direct Debit payments on this
                    account.
                  </Text>
                </View>
              </View>
            </TouchableBounceCard>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brand title="Continue" onPress={handleContinue} disabled={!checked} loading={requesting}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    img: {
        width: rem(80),
        height: rem(40),
        tintColor: colors.text.primary,
        alignSelf: 'flex-start',
    },
    noMarginTop: {
        marginTop: 0,
    },
}));
export default AutoinvestConfirmBankDetailsScreen;
